import '../../i18n/autoTranslate';
import React from 'react';
import Icon from '../../../components/AppIcon';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import { motion } from 'framer-motion';

import { FaBolt, FaUsers } from 'react-icons/fa';
import { FaStar, FaLaptopCode } from 'react-icons/fa';
import { IoBusinessSharp } from 'react-icons/io5';

const InterviewTypeCard = ({ type, isSelected, onSelect, className = '' }) => {
  const getIcon = (t) => {
    switch (t) {
      case 'behavioral':
        return <FaStar size={28} className="text-emerald-200" />;
      case 'technical':
        return <FaLaptopCode size={28} className="text-sky-200" />;
      case 'case_study':
        return <IoBusinessSharp size={28} className="text-violet-200" />;
      case 'stress':
        return <FaBolt size={28} className="text-rose-200" />;
      case 'panel':
        return <FaUsers size={28} className="text-indigo-200" />;
      default:
        return <FaStar size={28} className="text-emerald-200" />;
    }
  };

  const typeConfig = {
    behavioral: {
      title: 'Behavioral',
      description: 'STAR method questions about past experiences and situations',
      difficulty: 'Medium',
      image: '/assets/images/behavioral_qcy3va.png',
      spotlight: 'rgba(16, 185, 129, 0.22)',
    },
    technical: {
      title: 'Technical',
      description: 'Coding challenges, system design, and technical problem solving',
      difficulty: 'Hard',
      image: '/assets/images/technical.png',
      spotlight: 'rgba(56, 189, 248, 0.22)',
    },
    case_study: {
      title: 'Case Study',
      description: 'Business scenarios and analytical problem-solving exercises',
      difficulty: 'Hard',
      image: '/assets/images/Case_Study_jwfir6.png',
      spotlight: 'rgba(167, 139, 250, 0.22)',
    },
    stress: {
      title: 'Stress',
      description: 'High-pressure questions with time constraints and interruptions',
      difficulty: 'Very Hard',
      image: '/assets/images/Stress_Interview.png',
      spotlight: 'rgba(244, 63, 94, 0.22)',
    },
    panel: {
      title: 'Panel',
      description: 'Multiple AI interviewers with different questioning styles',
      difficulty: 'Hard',
      image: '/assets/images/Pannel_w5gfue.png',
      spotlight: 'rgba(99, 102, 241, 0.22)',
    },
    video: {
      title: 'Video',
      description: 'Full video simulation with visual and audio analysis',
      difficulty: 'Hard',
      image: '/assets/images/Gemini_Generated_5_k3kr9x.png',
      spotlight: 'rgba(56, 189, 248, 0.20)',
      comingSoon: true,
    },
  };

  const config = typeConfig?.[type];
  if (!config) return null;

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'text-emerald-200 bg-emerald-400/10 border-emerald-300/20';
      case 'Medium':
        return 'text-amber-200 bg-amber-400/10 border-amber-300/20';
      case 'Hard':
        return 'text-rose-200 bg-rose-400/10 border-rose-300/20';
      case 'Very Hard':
        return 'text-rose-100 bg-rose-500/10 border-rose-400/20';
      default:
        return 'text-white/70 bg-white/5 border-white/10';
    }
  };

  const disabled = Boolean(config?.comingSoon);

  return (
    <SpotlightCard
      spotlightColor={
        isSelected
          ? 'rgba(56, 189, 248, 0.28)'
          : config.spotlight || 'rgba(255,255,255,0.08)'
      }
      className={`group custom-spotlight-card h-full rounded-3xl overflow-hidden ${className}`}
    >
      <motion.button
        type="button"
        onClick={disabled ? undefined : () => onSelect(type)}
        disabled={disabled}
        whileHover={disabled ? undefined : { y: -3 }}
        whileTap={disabled ? undefined : { scale: 0.99 }}
        transition={{ type: 'spring', stiffness: 260, damping: 18 }}
        className={`
          relative w-full h-full overflow-hidden rounded-3xl border text-left
          backdrop-blur-xl transition-all duration-300
          bg-[#0b0f16]/70
          ${
            isSelected
              ? 'border-white/20 shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_22px_70px_-40px_rgba(56,189,248,0.60)]'
              : 'border-white/10 hover:border-white/16'
          }
          ${disabled ? 'cursor-not-allowed opacity-70 hover:border-white/10' : ''}
        `}
      >
        {/* background image layer */}
        {/* <div className="absolute inset-0 opacity-[0.26] pointer-events-none">
          <img
            src={config?.image}
            alt={config?.title}
            className="absolute inset-0 h-full w-full object-cover scale-110 group-hover:scale-125 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-black/65 to-black/85" />
        </div> */}

        {/* ambient */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-28 -right-28 h-64 w-64 rounded-full bg-sky-400/10 blur-[90px]" />
          <div className="absolute -bottom-32 -left-28 h-72 w-72 rounded-full bg-emerald-400/10 blur-[100px]" />
          <div className="absolute inset-0 opacity-60 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
        </div>

        {/* Selection / Coming soon */}
        {isSelected && (
          <div className="absolute top-4 right-4 w-8 h-8 rounded-2xl bg-emerald-400/15 border border-emerald-300/25 backdrop-blur-xl flex items-center justify-center shadow-[0_0_18px_rgba(16,185,129,0.45)]">
            <Icon name="Check" size={16} color="white" strokeWidth={2.5} />
          </div>
        )}

        {disabled && (
          <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[11px] font-medium border border-white/12 bg-white/[0.05] text-white/75 backdrop-blur-xl">
            Coming Soon
          </div>
        )}

        <div className="relative p-6 sm:p-7 flex flex-col min-h-[260px]">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/12 bg-white/[0.04] backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.03)]">
              {getIcon(type)}
            </div>
            <div className="mt-4 space-y-2">
              <h3 className="text-xl font-semibold tracking-tight text-white">
                {config?.title}
              </h3>
              <p className="text-sm text-white/70 leading-relaxed">
                {config?.description}
              </p>
            </div>
          </div>
        </div>

        {/* Difficulty ribbon */}
        <div className="absolute top-[78px] -left-6 min-w-[140px] -rotate-[46deg] origin-top-left">
          <div
            className={`px-3 py-1 text-center text-xs font-medium border shadow-sm backdrop-blur-xl ${getDifficultyColor(
              config?.difficulty
            )}`}
          >
            {config?.difficulty}
          </div>
        </div>
      </motion.button>
    </SpotlightCard>
  );
};

export default InterviewTypeCard;
